import { useEffect, useRef } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { KpiTileWall } from "./components/KpiTileWall";
import { MetricsFilterBar } from "./components/MetricsFilterBar";
import { PartnerDeepDive } from "./PartnerDeepDive";
import { PartnerMetricsTable } from "./components/PartnerMetricsTable";
import { PartnerMetricsTiles } from "./components/PartnerMetricsTiles";
import { useMetricsDashboard } from "./useMetricsDashboard";
import { PageLayoutDesktop } from "src/components/layout/PageLayoutDesktop";
import { PageLayoutMobile } from "src/components/layout/PageLayoutMobile";

/** Dense-table pages read at 1528 (twosion-ui / insights-home PageLayoutDesktop's maxWidthFullscreen); reading/form pages stay at the 1152 default. */
const DASHBOARD_MAX_WIDTH = 1528;

/**
 * Metrics Dashboard — the portfolio-wide view across all partners, and the
 * portal's landing page (route "/"). Ported from the POC's Metrics Dashboard.
 *
 * TODO(CPD-65): POC Metrics Dashboard sections not yet ported (each is its own
 * follow-up — see the CPD-65 description for the full list):
 *   - Partner Metrics Assistant (PMA) — suggested actions off the computed rows.
 *   - Portfolio quadrant chart (utilisation × N-day growth, bubble = ACV).
 *   - Growth-window selector (7 / 14 / 30 day) — a POC-only affordance, not a
 *     stated PRD requirement (just an open question about which fixed window
 *     to standardize on), so not planned unless product asks for it.
 *   - Per-column numeric range filters on the table (ACV, utilization %,
 *     etc.) — POC-only richness with no PRD backing; the free-text/type-ahead
 *     part of "searchable" is covered by the Partner/Region Autocompletes.
 *   - Real authenticated fetch: replace `fetchMetricsPortfolio`'s stub body
 *     (see src/features/metrics/metrics.api.ts).
 */
export function MetricsDashboardPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dashboardState = useMetricsDashboard();
  const dashboard = dashboardState.status === "success" ? dashboardState.dashboard : undefined;

  // Selecting a partner — from the Partner filter above or a table row/tile
  // click below — reveals the deep dive further down the page; bring it into
  // view the same way the POC's onMetricsPartnerSelect does, or picking one
  // has no visible effect until the user notices and scrolls down manually.
  const deepDiveRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (dashboard?.selection.partner) {
      deepDiveRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [dashboard?.selection.partner]);

  const title = (
    <Box>
      <Typography variant="h5" gutterBottom>
        Metrics Dashboard
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: isMobile ? 1 : 0 }}>
        Portfolio-wide contract, health, and engagement signals across all partners.
      </Typography>
    </Box>
  );

  const body = (
    <>
      {dashboardState.status === "pending" && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress aria-label="Loading metrics" />
        </Box>
      )}

      {dashboardState.status === "error" && (
        <Alert severity="error">
          Couldn&apos;t load partner metrics
          {dashboardState.error.message ? `: ${dashboardState.error.message}` : "."}
        </Alert>
      )}

      {dashboard && (
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <MetricsFilterBar
              filters={dashboard.filters.value}
              isDefault={dashboard.filters.isDefault}
              allRows={dashboard.filters.partnerOptions}
              tiers={dashboard.filters.tiers}
              onPartnerChange={dashboard.selection.selectPartner}
              onChange={dashboard.filters.set}
              onReset={dashboard.filters.reset}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              {dashboard.partners.rows.length} of {dashboard.partners.totalCount} partners shown
            </Typography>
          </Paper>

          <KpiTileWall groups={dashboard.insight.groups} />

          <Paper variant="outlined" sx={{ overflow: "hidden" }}>
            <Typography variant="subtitle2" sx={{ px: 2, pt: 2, pb: 1 }}>
              Partners
            </Typography>
            {isMobile ? (
              <PartnerMetricsTiles
                rows={dashboard.partners.rows}
                onSelectPartner={dashboard.selection.selectPartner}
              />
            ) : (
              <PartnerMetricsTable
                rows={dashboard.partners.rows}
                sort={dashboard.partners.sort}
                onSort={dashboard.partners.toggleSort}
                onSelectPartner={dashboard.selection.selectPartner}
              />
            )}
          </Paper>

          {dashboard.selection.partner && (
            <Box ref={deepDiveRef}>
              <PartnerDeepDive row={dashboard.selection.partner} />
            </Box>
          )}
        </Stack>
      )}
    </>
  );

  // twosion-ui convention: PageLayoutDesktop takes a `header` prop, but
  // PageLayoutMobile never does (checked ~20 call sites) — mobile pages fold
  // their own title into `content` instead.
  if (isMobile) {
    return (
      <PageLayoutMobile
        content={
          <>
            {title}
            {body}
          </>
        }
      />
    );
  }

  return <PageLayoutDesktop header={title} content={body} maxWidth={DASHBOARD_MAX_WIDTH} />;
}
